import React, { useEffect, useMemo, useCallback } from 'react'
import { Select, Grid, Text, Flexbox, Icon } from '@components/common'
import {
  useGetYearsQuery,
  useGetAssetQuery,
  useGetFiscalPeriodInfoQuery,
  useFetchPeriodsQuery,
} from '@api'
import { Period, DeprecProdUnit } from '@api/models'
import moment from 'moment'

interface ProductionUnitsPeriodSelectorProps {
  type: 'add' | 'edit'
  assetCompanyID: number
  assetID: string
  fiscalYear?: number
  fiscalPeriod?: number
  onYearChange: (year: number) => void
  onPeriodChange: (period: number, endDate: string) => void
  disabled?: boolean
  readonly?: boolean
  existingProductionUnits?: DeprecProdUnit[]
  onFirstAvailablePeriodChange?: (firstPeriod: number | null) => void
  onFirstAvailableYearChange?: (firstYear: number | null) => void
  errors?: {
    FiscalYear?: { message?: string }
    FiscalPeriod?: { message?: string }
  }
}

interface PeriodOption {
  label: string
  value: number
  endDate: string
  period: Period
}

const ProductionUnitsPeriodSelector: React.FC<ProductionUnitsPeriodSelectorProps> = ({
  type,
  assetCompanyID,
  assetID,
  fiscalYear,
  fiscalPeriod,
  onYearChange,
  onPeriodChange,
  disabled = false,
  readonly = false,
  existingProductionUnits = [],
  onFirstAvailablePeriodChange,
  onFirstAvailableYearChange,
  errors = {},
}) => {
  const { data: asset } = useGetAssetQuery({
    assetCompanyID,
    assetID,
  })

  const { data: years = [], isLoading: isLoadingYears } = useGetYearsQuery({
    assetCompanyID,
  })

  const { data: periods = [], isLoading: isLoadingPeriods } = useFetchPeriodsQuery(
    { assetCompanyID, year: fiscalYear! },
    { skip: !fiscalYear }
  )

  const { data: fiscalPeriodInfo } = useGetFiscalPeriodInfoQuery({
    assetCompanyID,
  })

  const createYearOptions = (yearsList: number[]) =>
    yearsList.map(year => ({ label: year.toString(), value: year }))

  const createPeriodOptions = (periodsList: Period[]): PeriodOption[] =>
    periodsList.map(period => ({
      label: `Period ${period.CP_PERIOD} (ends ${moment(period.CP_DATE).format(
        'MMM DD, YYYY'
      )})`,
      value: period.CP_PERIOD,
      endDate: period.CP_DATE,
      period,
    }))

  const filteredYears = useMemo(() => {
    let filteredYearsList = years

    if (asset?.AS_REC_DAT) {
      const assetRecordYear = moment(asset.AS_REC_DAT).year()
      filteredYearsList = filteredYearsList.filter(year => year >= assetRecordYear)
    }

    if (fiscalPeriodInfo?.FirstOpenFiscalYear) {
      filteredYearsList = filteredYearsList.filter(
        year => year >= fiscalPeriodInfo.FirstOpenFiscalYear
      )
    }

    if (type !== 'edit' && existingProductionUnits.length > 0) {
      const earliestYear = Math.min(...existingProductionUnits.map(unit => unit.FiscalYear))
      filteredYearsList = filteredYearsList.filter(year => year >= earliestYear)
    }

    return filteredYearsList
  }, [
    years,
    asset?.AS_REC_DAT,
    fiscalPeriodInfo?.FirstOpenFiscalYear,
    type,
    existingProductionUnits,
  ])

  const filteredPeriods = useMemo(() => {
    let filteredPeriodsList = periods

    if (asset?.AS_REC_DAT) {
      const assetRecordDate = moment(asset.AS_REC_DAT)
      filteredPeriodsList = filteredPeriodsList.filter(period =>
        moment(period.CP_DATE).isSameOrAfter(assetRecordDate, 'day')
      )
    }

    if (fiscalPeriodInfo?.FirstOpenFiscalYear && fiscalPeriodInfo?.FirstOpenFiscalPeriod) {
      if (fiscalYear === fiscalPeriodInfo.FirstOpenFiscalYear) {
        filteredPeriodsList = filteredPeriodsList.filter(
          period => period.CP_PERIOD >= fiscalPeriodInfo.FirstOpenFiscalPeriod
        )
      } else if (fiscalYear && fiscalYear > fiscalPeriodInfo.FirstOpenFiscalYear) {
      } else if (!fiscalYear || fiscalYear < fiscalPeriodInfo.FirstOpenFiscalYear) {
        filteredPeriodsList = []
      }
    }

    if (type !== 'edit' && fiscalYear && existingProductionUnits.length > 0) {
      const earliestUnit = existingProductionUnits[0]

      if (fiscalYear < earliestUnit.FiscalYear) {
        filteredPeriodsList = []
      } else if (fiscalYear === earliestUnit.FiscalYear) {
        filteredPeriodsList = filteredPeriodsList.filter(
          period => period.CP_PERIOD >= earliestUnit.FiscalPeriod
        )

        const existingPeriodsForYear = existingProductionUnits
          .filter(unit => unit.FiscalYear === fiscalYear)
          .map(unit => unit.FiscalPeriod)

        filteredPeriodsList = filteredPeriodsList.filter(
          period => !existingPeriodsForYear.includes(period.CP_PERIOD)
        )
      } else {
        const existingPeriodsForYear = existingProductionUnits
          .filter(unit => unit.FiscalYear === fiscalYear)
          .map(unit => unit.FiscalPeriod)

        filteredPeriodsList = filteredPeriodsList.filter(
          period => !existingPeriodsForYear.includes(period.CP_PERIOD)
        )
      }
    }

    return filteredPeriodsList
  }, [
    periods,
    asset?.AS_REC_DAT,
    fiscalPeriodInfo?.FirstOpenFiscalYear,
    fiscalPeriodInfo?.FirstOpenFiscalPeriod,
    fiscalYear,
    existingProductionUnits,
    type,
  ])

  const yearOptions = useMemo(() => createYearOptions(filteredYears), [filteredYears])
  const periodOptions = useMemo(() => createPeriodOptions(filteredPeriods), [
    filteredPeriods,
  ])

  useEffect(() => {
    if (onFirstAvailablePeriodChange && type === 'add') {
      const firstAvailablePeriod =
        filteredPeriods.length > 0 ? filteredPeriods[0].CP_PERIOD : null
      onFirstAvailablePeriodChange(firstAvailablePeriod)
    }
  }, [filteredPeriods, onFirstAvailablePeriodChange, type, fiscalYear])

  useEffect(() => {
    if (onFirstAvailableYearChange && type === 'add') {
      const firstAvailableYear = filteredYears.length > 0 ? filteredYears[0] : null
      onFirstAvailableYearChange(firstAvailableYear)
    }
  }, [filteredYears, onFirstAvailableYearChange, type])

  const selectedYear = fiscalYear
    ? { label: fiscalYear.toString(), value: fiscalYear }
    : null
  const selectedPeriod = fiscalPeriod
    ? periodOptions.find(opt => opt.value === fiscalPeriod) || null
    : null

  const handleYearChange = useCallback(
    (option: { label: string; value: number }) => {
      onYearChange(option.value)
      if (fiscalYear !== option.value && fiscalPeriod) {
        onPeriodChange(0, '')
      }
    },
    [onYearChange, onPeriodChange, fiscalYear, fiscalPeriod]
  )

  const handlePeriodChange = useCallback(
    (option: PeriodOption) => {
      onPeriodChange(option.value, option.endDate)
    },
    [onPeriodChange]
  )

  const createSelectProps = (
    label: string,
    name: string,
    options: any[],
    value: any,
    onChange: (option: any) => void,
    extraDisabled = false,
    error?: boolean,
    errorMsg?: string
  ) => ({
    label,
    name,
    options,
    value,
    onChange,
    disabled: disabled || extraDisabled,
    readOnly: readonly,
    placeholder:
      isLoadingPeriods && name.includes('Period')
        ? 'Loading periods...'
        : `Select ${label.toLowerCase()}`,
    type: 'autocomplete' as const,
    isClearable: false,
    error,
    errorMsg,
  })

  return (
    <>
      <Grid.Item m={6}>
        <Select
          {...createSelectProps(
            'Fiscal Year',
            'fiscalYear',
            yearOptions,
            selectedYear,
            handleYearChange,
            isLoadingYears,
            !!errors.FiscalYear,
            errors.FiscalYear?.message
          )}
        />
      </Grid.Item>

      {fiscalYear && periodOptions.length === 0 && type !== 'edit' ? (
        <Grid.Item
          m={6}
          style={{
            height: '100%',
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Flexbox
            alignItems="center"
            flexDirection="row"
            justifyContent="center"
            gap={6}
            style={{ marginTop: '18px' }}
          >
            <Icon name="AlertCircle" color="var(--danger-color)" size="m" />
            <Text>All periods have been used for this fiscal year</Text>
          </Flexbox>
        </Grid.Item>
      ) : (
        <Grid.Item m={6}>
          <Select
            {...createSelectProps(
              'Fiscal Period',
              'fiscalPeriod',
              periodOptions,
              selectedPeriod,
              handlePeriodChange,
              !fiscalYear || isLoadingPeriods,
              !!errors.FiscalPeriod,
              errors.FiscalPeriod?.message
            )}
          />
        </Grid.Item>
      )}
    </>
  )
}

export default ProductionUnitsPeriodSelector
